import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Divider,
  IconButton,
  Card,
  CardMedia,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Timer as TimerIcon,
  Restaurant as ServingsIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  FiberManualRecord as BulletIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Lock as PrivateIcon,
  People as FamilyIcon,
  Share as ShareIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';

/**
 * RecipeDetailDialog Component
 * Displays full recipe details including ingredients, instructions, and metadata
 */
const RecipeDetailDialog = ({ open, onClose, recipe, onEdit, onDelete, onArchive }) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();

  if (!recipe) return null;

  const {
    title,
    description,
    category,
    ingredients = [],
    instructions = [],
    prepTime,
    cookTime,
    servings,
    difficulty,
    photoURL,
    createdBy,
    createdById,
    createdAt,
    visibility = 'family',
    sharedWith = [],
    taggedMembers = [],
    isArchived = false,
  } = recipe;

  const totalTime = (parseInt(prepTime) || 0) + (parseInt(cookTime) || 0);

  // Check if current user can edit (creator or admin)
  const canEdit = currentUser?.uid === createdById || profile?.isAdmin || profile?.role === 'owner';

  // Get visibility display info
  const getVisibilityInfo = () => {
    switch (visibility) {
      case 'private':
        return { icon: <PrivateIcon fontSize="small" />, label: 'Private', color: 'warning' };
      case 'shared':
        return { icon: <ShareIcon fontSize="small" />, label: `Shared with ${sharedWith.length} people`, color: 'info' };
      default:
        return { icon: <FamilyIcon fontSize="small" />, label: 'Family', color: 'success' };
    }
  };

  const visibilityInfo = getVisibilityInfo();

  const handleEdit = () => {
    if (onEdit) {
      onEdit(recipe);
    }
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(recipe);
    }
  };

  const handleArchive = () => {
    if (onArchive) {
      onArchive(recipe);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      {/* Header */}
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flexGrow: 1, pr: 2 }}>
            <Typography variant="h5" component="div" sx={{ fontWeight: 600, mb: 1 }}>
              {title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {category && (
                <Chip
                  icon={<CategoryIcon />}
                  label={category}
                  size="small"
                  color="warning"
                />
              )}
              {difficulty && (
                <Chip
                  label={difficulty}
                  size="small"
                  color={
                    difficulty === 'Easy' ? 'success' :
                    difficulty === 'Hard' ? 'error' : 'default'
                  }
                />
              )}
              <Tooltip title={visibilityInfo.label}>
                <Chip
                  icon={visibilityInfo.icon}
                  label={visibilityInfo.label}
                  size="small"
                  color={visibilityInfo.color}
                  variant="outlined"
                />
              </Tooltip>
              {isArchived && (
                <Chip
                  icon={<ArchiveIcon />}
                  label="Archived"
                  size="small"
                  color="warning"
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {canEdit && (
              <>
                <Tooltip title="Edit Recipe">
                  <IconButton onClick={handleEdit} color="primary">
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isArchived ? "Restore Recipe" : "Archive Recipe"}>
                  <IconButton onClick={handleArchive} color="warning">
                    {isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete Recipe">
                  <IconButton onClick={handleDelete} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Recipe Photo */}
        {photoURL && (
          <Card sx={{ mb: 3 }}>
            <CardMedia
              component="img"
              image={photoURL}
              alt={title}
              sx={{
                maxHeight: 400,
                objectFit: 'cover',
              }}
            />
          </Card>
        )}

        {/* Description */}
        {description && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary">
              {description}
            </Typography>
          </Box>
        )}

        {/* Tagged Members */}
        {taggedMembers && taggedMembers.length > 0 && (
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <TagIcon sx={{ color: 'info.main', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              Tagged:
            </Typography>
            {taggedMembers.map((memberId, index) => (
              <Chip
                key={index}
                icon={<PersonIcon />}
                label={memberId}
                size="small"
                variant="outlined"
                color="info"
              />
            ))}
          </Box>
        )}

        {/* Recipe Info */}
        <Box sx={{ mb: 3, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {prepTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Prep Time
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {prepTime} min
                </Typography>
              </Box>
            </Box>
          )}
          {cookTime && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Cook Time
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {cookTime} min
                </Typography>
              </Box>
            </Box>
          )}
          {totalTime > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TimerIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Total Time
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {totalTime} min
                </Typography>
              </Box>
            </Box>
          )}
          {servings && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ServingsIcon sx={{ color: 'text.secondary' }} />
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Servings
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {servings}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Ingredients */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Ingredients
          </Typography>
          <List dense sx={{ bgcolor: 'background.default', borderRadius: 1, p: 2 }}>
            {ingredients.map((ingredient, index) => (
              <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <BulletIcon sx={{ fontSize: 8, color: 'primary.main' }} />
                </ListItemIcon>
                <ListItemText
                  primary={ingredient}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Instructions */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Instructions
          </Typography>
          <List sx={{ bgcolor: 'background.default', borderRadius: 1, p: 2 }}>
            {instructions.map((instruction, index) => (
              <ListItem
                key={index}
                alignItems="flex-start"
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom: index < instructions.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                }}
              >
                <Box
                  sx={{
                    mr: 2,
                    minWidth: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                  }}
                >
                  {index + 1}
                </Box>
                <ListItemText
                  primary={instruction}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        {/* Footer Info */}
        {(createdBy || createdAt) && (
          <>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                Shared by {createdBy}
                {createdAt && createdAt.toDate && ` • ${formatDistanceToNow(createdAt.toDate(), { addSuffix: true })}`}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        {canEdit && (
          <>
            <Button onClick={handleEdit} startIcon={<EditIcon />} color="primary">
              Edit
            </Button>
            <Button
              onClick={handleArchive}
              startIcon={isArchived ? <UnarchiveIcon /> : <ArchiveIcon />}
              color="warning"
            >
              {isArchived ? 'Restore' : 'Archive'}
            </Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RecipeDetailDialog;
